import { PmsTooltip } from '@/components/ui/pms-tooltip';
import { EditorCanvas } from '@/components/words/editor/editor-canvas';
import { QuickAddLinksModal } from '@/components/words/editor/quick-add-links-modal';
import { FunctionLetter } from '@/components/words/function-letter';
import { ReasoningSteps } from '@/components/words/reasoning-steps';
import { RoundTagGroup } from '@/components/words/round-tag-group';
import { SeeAlsoEditor } from '@/components/words/see-also-editor';
import { TagEditor } from '@/components/words/tag-editor';
import { ToCheckBanner } from '@/components/words/to-check-banner';
import { WordDetailSkeleton } from '@/components/words/word-detail-skeleton';
import { WordShellTopbar } from '@/components/words/word-shell-topbar';
import { wordsApi } from '@/lib/api';
import { formatRevisedDate } from '@/lib/format-date';
import { extractInternalLinkIds, parseSeeAlsoEntries } from '@/lib/word-links';
import { useWordsNav } from '@/lib/words-nav';
import type {
  NoyauCode,
  NoyauPonderation,
  ReasoningSteps as ReasoningStepsTuple,
  Word,
  WordSummary,
  WordTag,
} from '@/schemas/word';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

/** Safety backstop for the quick-add full-catalog scan (mirrors words-nav.tsx's RESULTS_CAP). */
const QUICK_ADD_CATALOG_CAP = 2000;

interface WordDetailProps {
  wordId: string;
}

export function WordDetail({ wordId }: WordDetailProps) {
  const nav = useWordsNav();

  const {
    data: word,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['word', wordId],
    queryFn: () => wordsApi.get(wordId),
    staleTime: 60_000,
    retry: 1,
  });

  if (isLoading) {
    return (
      <div>
        <WordShellTopbar showListBack />
        <div style={{ padding: '10px 32px' }}>
          <WordDetailSkeleton />
        </div>
      </div>
    );
  }

  if (isError || !word) {
    return (
      <div className="words-empty" style={{ height: '100%' }}>
        <span className="material-symbols-rounded words-empty__icon">sentiment_dissatisfied</span>
        <p>Mot introuvable.</p>
      </div>
    );
  }

  return <WordDetailEditor key={word.id} word={word} nav={nav} />;
}

interface WordDetailEditorProps {
  word: Word;
  nav: ReturnType<typeof useWordsNav>;
}

type SaveStatus = 'saved' | 'saving' | 'error';

/** Owns the word editing state: body content, tags, "Voir aussi" links, noyau sources, auto-save, read-only toggle, and the wordList query used to resolve and colour internal links. */
function WordDetailEditor({ word, nav }: WordDetailEditorProps) {
  const queryClient = useQueryClient();
  const [content, setContent] = useState<string>(word.content);
  const [tags, setTags] = useState<WordTag[]>(word.tags);
  const [noyauPonderation, setNoyauPonderation] = useState<NoyauPonderation>(word.noyauPonderation);
  const [noyauSing, setNoyauSing] = useState<NoyauCode | null>(word.noyauSing);
  const [noyauValidated, setNoyauValidated] = useState<boolean>(word.noyauValidated);
  const [reasoningSteps, setReasoningSteps] = useState<ReasoningStepsTuple>(word.reasoningSteps);
  const [seeAlso, setSeeAlso] = useState<string>(word.seeAlso);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [readOnly, setReadOnly] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const latestContentRef = useRef<string>(word.content);
  const latestTagsRef = useRef<WordTag[]>(word.tags);
  const latestNoyauPonderationRef = useRef<NoyauPonderation>(word.noyauPonderation);
  const latestNoyauSingRef = useRef<NoyauCode | null>(word.noyauSing);
  const latestNoyauValidatedRef = useRef<boolean>(word.noyauValidated);
  const latestReasoningStepsRef = useRef<ReasoningStepsTuple>(word.reasoningSteps);
  // Lazy persist (Point 4): only send reasoningSteps in the PATCH once a step
  // has actually been edited this session — never just because the word was opened.
  const reasoningStepsDirtyRef = useRef(false);
  const latestSeeAlsoRef = useRef<string>(word.seeAlso);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Default word page — used by the editor to resolve and colour internal links.
  // This is a single capped/alphabetical page, NOT the full database (see the
  // targeted `referencedWords` fetch below for why that matters).
  const { data: wordList = [] } = useQuery<WordSummary[]>({
    queryKey: ['words', 'all'],
    queryFn: () => wordsApi.search().then((page) => page.items),
    staleTime: 60_000,
  });

  // The words this word's own content links / "voir aussi" list actually
  // reference, resolved by exact numeric-id lookup regardless of where they'd
  // land in the capped default page above. Without this, a link to a word
  // outside that page renders dangling (grey, non-clickable) even though the
  // word exists in the database. One batched request, not a search per word —
  // ids have no embedded whitespace, so (unlike title) the exact/comma-OR
  // grammar batches them cleanly (see wordsApi.getByIds).
  const referencedIds = useMemo(
    () => [
      ...new Set([
        ...extractInternalLinkIds(word.content),
        ...parseSeeAlsoEntries(word.seeAlso)
          .map((e) => e.id)
          .filter((id): id is string => id !== null),
      ]),
    ],
    [word.content, word.seeAlso],
  );
  const referencedQuery = useQuery<WordSummary[]>({
    queryKey: ['words', 'referenced', referencedIds],
    queryFn: () => wordsApi.getByIds(referencedIds),
    enabled: referencedIds.length > 0,
    staleTime: 60_000,
  });
  const referencedWords = referencedQuery.data ?? [];
  // Explicit "settled" signal for EditorCanvas's one-time recolor — see its
  // `linksReady` prop doc for why this can't be inferred from wordList's array
  // identity (the `data = []` query default is a fresh-but-unchanged array on
  // every render while loading).
  const linksReady =
    referencedIds.length === 0 || referencedQuery.isSuccess || referencedQuery.isError;
  const resolvedWordList = useMemo(() => {
    if (referencedWords.length === 0) return wordList;
    return [...new Map([...wordList, ...referencedWords].map((w) => [w.id, w])).values()];
  }, [wordList, referencedWords]);

  // Quick-add scans the FULL word catalog for linkable titles (not just the default
  // page), so it's fetched lazily — only once the user actually opens the modal.
  const { data: fullWordCatalog = { words: [] as WordSummary[], truncated: false } } = useQuery({
    queryKey: ['words', 'full-catalog'],
    queryFn: () =>
      wordsApi
        .searchUpTo(undefined, undefined, QUICK_ADD_CATALOG_CAP)
        .then((batch) => ({ words: batch.items, truncated: batch.truncated })),
    enabled: quickAddOpen,
    staleTime: 60_000,
  });

  const patchMutation = useMutation({
    mutationFn: () =>
      // Keyed by the Tahiti guid (Word.id); slug is cosmetic only.
      wordsApi.patch(word.id, {
        content: latestContentRef.current,
        tags: latestTagsRef.current,
        noyauPonderation: latestNoyauPonderationRef.current,
        noyauSing: latestNoyauSingRef.current,
        noyauValidated: latestNoyauValidatedRef.current,
        ...(reasoningStepsDirtyRef.current
          ? { reasoningSteps: latestReasoningStepsRef.current }
          : {}),
        seeAlso: latestSeeAlsoRef.current,
      }),
    onSuccess: (updated) => {
      setSaveStatus('saved');
      queryClient.setQueryData(['word', word.id], updated);
    },
    onError: () => setSaveStatus('error'),
  });

  function scheduleSave() {
    setSaveStatus('saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      patchMutation.mutate();
    }, 1500);
  }

  /** Validate/un-validate save immediately (a deliberate, already-confirmed action) instead of debouncing. */
  function saveNow() {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    patchMutation.mutate();
  }

  function handleContentChange(newContent: string) {
    setContent(newContent);
    latestContentRef.current = newContent;
    scheduleSave();
  }

  function handleTagsChange(newTags: WordTag[]) {
    setTags(newTags);
    latestTagsRef.current = newTags;
    scheduleSave();
  }

  function handleNoyauPonderationChange(next: NoyauPonderation) {
    setNoyauPonderation(next);
    latestNoyauPonderationRef.current = next;
    scheduleSave();
  }

  // Point 8: validate locks the noyau to `code` and disables ponderation editing.
  function handleValidateNoyau(code: NoyauCode) {
    setNoyauSing(code);
    setNoyauValidated(true);
    latestNoyauSingRef.current = code;
    latestNoyauValidatedRef.current = true;
    saveNow();
  }

  // Point 8: un-validate re-enables ponderation editing; noyau_sing is left as-is.
  function handleUnvalidateNoyau() {
    setNoyauValidated(false);
    latestNoyauValidatedRef.current = false;
    saveNow();
  }

  // Point 4: a step was actually edited — mark dirty so the next save includes it.
  function handleReasoningStepsChange(next: ReasoningStepsTuple) {
    setReasoningSteps(next);
    latestReasoningStepsRef.current = next;
    reasoningStepsDirtyRef.current = true;
    scheduleSave();
  }

  // Update the "Voir aussi" markdown and trigger the same debounced auto-save as blocks/tags.
  function handleSeeAlsoChange(next: string) {
    setSeeAlso(next);
    latestSeeAlsoRef.current = next;
    scheduleSave();
  }

  return (
    <>
      <WordShellTopbar showListBack>
        {!readOnly && (
          <PmsTooltip label="Lier automatiquement les mots" side="bottom">
            <button
              type="button"
              className="words-topbar__quick-add"
              aria-label="Ajouter des liens automatiques"
              onClick={() => setQuickAddOpen(true)}
            >
              <span className="material-symbols-rounded">auto_awesome</span>
            </button>
          </PmsTooltip>
        )}

        {!readOnly && (
          <div className="words-topbar__save" aria-live="polite">
            {saveStatus === 'saving' && (
              <>
                <span className="material-symbols-rounded words-topbar__save-icon words-topbar__save-icon--saving">
                  sync
                </span>
                Enregistrement…
              </>
            )}
            {saveStatus === 'saved' && (
              <>
                <span className="material-symbols-rounded words-topbar__save-icon">
                  check_circle
                </span>
                Enregistré
              </>
            )}
            {saveStatus === 'error' && (
              <>
                <span className="material-symbols-rounded words-topbar__save-icon words-topbar__save-icon--error">
                  error
                </span>
                Erreur d'enregistrement
              </>
            )}
          </div>
        )}

        <PmsTooltip
          label={readOnly ? 'Passer en mode édition' : 'Passer en mode lecture'}
          side="bottom"
        >
          <button
            type="button"
            className={`words-topbar__mode-toggle${readOnly ? ' is-readonly' : ''}`}
            aria-label={
              readOnly ? 'Mode lecture actif — cliquer pour éditer' : 'Passer en mode lecture'
            }
            aria-pressed={readOnly}
            onClick={() => setReadOnly((r) => !r)}
          >
            <span className="material-symbols-rounded">{readOnly ? 'visibility' : 'edit'}</span>
          </button>
        </PmsTooltip>
      </WordShellTopbar>

      <article className="words-page">
        <header className="words-page-header">
          <div className="words-page-title-col">
            <h2 className="words-page-title">
              {word.title}
              <FunctionLetter fonction={word.fonction} />
            </h2>
            <ToCheckBanner toCheck={word.toCheck} reasons={word.reasons} />
          </div>
          <RoundTagGroup
            noyauPonderation={noyauPonderation}
            editable={!readOnly}
            onChange={handleNoyauPonderationChange}
            noyauSing={noyauSing}
            noyauValidated={noyauValidated}
            onValidate={handleValidateNoyau}
            onUnvalidate={handleUnvalidateNoyau}
          />
        </header>

        <TagEditor tags={tags} readOnly={readOnly} onChange={handleTagsChange} />

        <section className="words-body">
          <EditorCanvas
            content={content}
            wordList={resolvedWordList}
            linksReady={linksReady}
            readOnly={readOnly}
            onChange={handleContentChange}
          />
        </section>

        <ReasoningSteps
          steps={reasoningSteps}
          readOnly={readOnly}
          onChange={handleReasoningStepsChange}
        />

        <SeeAlsoEditor
          seeAlso={seeAlso}
          wordList={resolvedWordList}
          currentSlug={word.slug}
          readOnly={readOnly}
          onChange={handleSeeAlsoChange}
          onNavigate={nav.pushWord}
        />

        <footer className="words-page-foot">
          <span>
            <span className="words-page-foot__key">Révisé</span>
            {formatRevisedDate(word.revisedAt)}
          </span>
        </footer>
      </article>

      {quickAddOpen && (
        <QuickAddLinksModal
          content={content}
          wordList={fullWordCatalog.words}
          truncated={fullWordCatalog.truncated}
          onApply={handleContentChange}
          onClose={() => setQuickAddOpen(false)}
        />
      )}
    </>
  );
}
